import React, { forwardRef } from 'react';
import { Menu } from '@headlessui/react';
import { motion } from 'framer-motion';

const AvatarUserMenu = forwardRef(function AvatarUserMenu(
  {
    currentUser,
    userRole,
    isDarkMode,
    primaryColor,
    primaryContrastText,
    getUserInitial,
    logoutLoading,
    onNavigate,
    onLogout,
  },
  ref
) {
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
  const panelBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const textPrimary = isDarkMode ? '#fff' : '#111827';
  const textMuted = isDarkMode ? 'rgba(255, 255, 255, 0.55)' : 'rgba(71, 85, 105, 0.9)';

  const displayName =
    currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Account';

  const menuItems = [
    { id: 'profile', label: 'Profile', path: '/profile' },
    { id: 'account', label: 'Account Security', path: '/account-management' },
  ];

  if (userRole !== 'landlord') {
    menuItems.push({ id: 'settings', label: 'Settings', path: '/settings' });
  }

  const menuButtonStyle = (active, danger = false) => ({
    display: 'block',
    width: 'calc(100% - 16px)',
    margin: '0 8px',
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    border: '1px solid',
    borderColor: active
      ? danger
        ? 'rgba(239, 68, 68, 0.35)'
        : primaryColor
      : 'transparent',
    borderRadius: '10px',
    cursor: logoutLoading && danger ? 'wait' : 'pointer',
    backgroundColor: active
      ? danger
        ? isDarkMode
          ? 'rgba(239, 68, 68, 0.15)'
          : 'rgba(239, 68, 68, 0.08)'
        : `${primaryColor}18`
      : 'transparent',
    color: danger ? '#EF4444' : active ? primaryColor : textPrimary,
  });

  return (
    <Menu.Items
      ref={ref}
      className="absolute right-0 z-50 mt-2 w-64 focus:outline-none topbar-v2__avatar-menu"
      style={{
        borderRadius: '16px',
        backgroundColor: panelBg,
        border: `1px solid ${borderColor}`,
        boxShadow: isDarkMode
          ? '0 24px 48px rgba(0, 0, 0, 0.45)'
          : '0 24px 48px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '16px',
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {currentUser?.photoURL ? (
            <img
              src={currentUser.photoURL}
              alt=""
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 600,
                backgroundColor: primaryColor,
                color: primaryContrastText,
              }}
            >
              {getUserInitial()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 600,
                color: textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </p>
            {currentUser?.email && (
              <p
                style={{
                  margin: '2px 0 0',
                  fontSize: 12,
                  color: textMuted,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentUser.email}
              </p>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: '8px 0' }}>
        {menuItems.map((item) => (
          <Menu.Item key={item.id}>
            {({ active }) => (
              <button
                type="button"
                style={menuButtonStyle(active)}
                onClick={() => onNavigate(item.path)}
              >
                {item.label}
              </button>
            )}
          </Menu.Item>
        ))}
      </div>

      <div
        style={{
          padding: '8px 0',
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              style={menuButtonStyle(active, true)}
              onClick={onLogout}
              disabled={logoutLoading}
            >
              {logoutLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      border: '2px solid #EF4444',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                    }}
                  />
                  Logging out…
                </span>
              ) : (
                'Sign out'
              )}
            </button>
          )}
        </Menu.Item>
      </div>
    </Menu.Items>
  );
});

export default AvatarUserMenu;
